import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useSite } from "./SiteContext";
import { waLink } from "../lib/api";

const COLS = [
  {
    title: "Travel",
    links: [
      ["International Holidays", "/international"],
      ["India Holidays", "/india"],
      ["Signature Journeys", "/journeys"],
      ["Curated Experiences", "/experiences"],
      ["Travel Styles", "/travel-styles"],
      ["Seasonal Collections", "/collections"],
    ],
  },
  {
    title: "Services",
    links: [
      ["Corporate Travel", "/corporate"],
      ["MICE & Offsites", "/corporate#mice"],
      ["Visa Services", "/visa"],
      ["AI Trip Planner", "/plan-my-journey"],
      ["Flights", "/contact?intent=flights"],
      ["Forex & Insurance", "/contact?intent=forex"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About Azulo", "/about"],
      ["The Journal", "/blog"],
      ["Contact", "/contact"],
      ["Destination Weddings", "/contact?intent=wedding"],
      ["Cruises", "/contact?intent=cruises"],
    ],
  },
];

export default function Footer() {
  const { settings } = useSite();
  return (
    <footer data-testid="site-footer" className="border-t border-[var(--line)] bg-[var(--paper-2)]">
      <div className="az-container py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <img
              src="/azulo-logo.webp"
              alt="Azulo Travel"
              width={2000}
              height={571}
              className="h-11 w-auto"
              loading="lazy"
            />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-[var(--muted-ink)]">
              {settings.footer_note}
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-[var(--ink-2)]">
              {settings.phone && (
                <a
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-2.5 hover:text-[var(--brass-2)]"
                  data-testid="footer-phone"
                >
                  <Phone className="h-4 w-4 text-[var(--brass)]" />
                  {settings.phone}
                </a>
              )}
              {settings.email && (
                <a
                  href={`mailto:${settings.email}`}
                  className="flex items-center gap-2.5 hover:text-[var(--brass-2)]"
                  data-testid="footer-email"
                >
                  <Mail className="h-4 w-4 text-[var(--brass)]" />
                  {settings.email}
                </a>
              )}
              {settings.address && (
                <p className="flex items-start gap-2.5">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brass)]" />
                  <span className="whitespace-pre-line">{settings.address}</span>
                </p>
              )}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <p className="eyebrow mb-4">{col.title}</p>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-sm text-[var(--ink-2)] transition-colors hover:text-[var(--brass-2)]"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <hr className="rule my-10" />

        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="text-xs leading-relaxed text-[var(--muted-ink)]">
            <p>
              &copy; {new Date().getFullYear()}{" "}
              {settings.parent_company || "Marwon Hospitality Pvt. Ltd."} &nbsp;All rights reserved.
            </p>
            <p className="mt-1">
              GSTIN {settings.gstin} &nbsp;&middot;&nbsp; CIN {settings.cin}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[
              [settings.instagram, Instagram, "instagram"],
              [settings.linkedin, Linkedin, "linkedin"],
              [settings.facebook, Facebook, "facebook"],
            ].map(([href, Icon, key]) =>
              href ? (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={key}
                  data-testid={`footer-social-${key}`}
                  className="rounded-full border border-[var(--line-strong)] bg-[var(--surface)] p-2.5 text-[var(--ink-2)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ) : null
            )}
            <a
              href={waLink(settings.whatsapp, "Hello Azulo Travel, I would like to plan a journey.")}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-whatsapp"
              className="flex items-center gap-2 rounded-full border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-2.5 text-xs font-semibold text-[var(--ink-2)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
